import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGridPremium, GridToolbarColumnsButton, GridToolbarContainer } from '@mui/x-data-grid-premium';
import { ITEM_COLUMNS, MAIN_EXTRA_WIDTHS, MAIN_HIDDEN_COLUMNS, UNIT_FIELDS, withUnit } from '../../../lib/packingListFields';
import { remainingQty } from '../../../lib/packingListAllocation';
import { formatQuantity, secondaryQuantities } from '../../../lib/packingListQuantity';

/** 보이는 열이 표 너비를 꽉 채우도록 기본 너비 비율대로 늘리고 줄인다. 기본 너비 아래로는 줄이지 않아 좁으면 가로 스크롤이 생긴다. #은 고정. */
const fitWidth = (field, width) => (field === 'line_no' ? { width, minWidth: width } : { flex: width, minWidth: width });

/** Description 칸. Local Description이 있으면 따로 열을 두지 않고 그 아래 줄에 회색으로 붙인다. */
function DescriptionCell({ row }) {
    return (
        <Box sx={{ minWidth: 0, lineHeight: 1.3 }}>
            <Typography sx={{ fontSize: 11 }} noWrap>{row.description}</Typography>
            {row.description_alt && <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{row.description_alt}</Typography>}
        </Box>
    );
}

const INITIAL_STATE = { columns: { columnVisibilityModel: Object.fromEntries(MAIN_HIDDEN_COLUMNS.map((field) => [field, false])) } };

/** 표 위 툴바. 컬럼 표시·숨김은 DataGrid 기본 Columns 패널로 한다. */
function ColumnsToolbar() {
    return (
        <GridToolbarContainer sx={{ px: 1, py: 0.25, bgcolor: '#f1f1f1' }}>
            <GridToolbarColumnsButton slotProps={{ button: { size: 'small', sx: { fontSize: 11 } } }} />
        </GridToolbarContainer>
    );
}

/** 확정된 il_pl_item 전체. Qty는 대표 수량과 단위("4 PCS")이고 나머지 수량은 옆 열에 모은다. 중량·부피도 값에 단위를 붙인다. 보일 컬럼은 Columns 패널에서 고르고(처음에는 MAIN_HIDDEN_COLUMNS를 숨김), 미배분 필터가 켜지면 잔량이 남은 라인만 보인다. */
export default function ItemsDataTable({ items, allocations, filterUnallocated }) {
    const rows = useMemo(() => {
        const list = items.map((it) => ({ ...it, other_quantities: secondaryQuantities(it.quantities).map(formatQuantity).join(', ') }));
        return filterUnallocated ? list.filter((r) => remainingQty(r, allocations) > 0) : list;
    }, [items, allocations, filterUnallocated]);

    const columns = useMemo(
        () => [
            ...ITEM_COLUMNS.filter((c) => !UNIT_FIELDS.has(c.field) && c.field !== 'description_alt').flatMap((c) => {
                const col = { field: c.field, headerName: c.headerName.replace(' *', ''), ...fitWidth(c.field, c.mainWidth), type: c.type === 'number' ? 'number' : 'string', headerAlign: 'center', ...(c.align && { align: c.align }), ...(c.unitField && { valueFormatter: (value, row) => withUnit(value, row[c.unitField]) }), ...(c.field === 'description' && { display: 'flex', renderCell: (p) => <DescriptionCell row={p.row} /> }) };
                return c.field === 'qty' ? [col, { field: 'other_quantities', headerName: 'Other Qty', ...fitWidth('other_quantities', MAIN_EXTRA_WIDTHS.other_quantities), headerAlign: 'center' }] : [col];
            }),
        ],
        [],
    );

    return (
        <Box sx={{ border: '1px solid #e6ebee', borderRadius: '8px', bgcolor: 'white', overflow: 'hidden' }}>
            <DataGridPremium
                rows={rows}
                columns={columns}
                initialState={INITIAL_STATE}
                slots={{ toolbar: ColumnsToolbar }}
                density="compact"
                hideFooter
                disableColumnMenu
                autoHeight                 rowHeight={28}
                getRowHeight={({ model }) => (model.description_alt ? 40 : null)}
                columnHeaderHeight={32}
                sx={{ border: 'none', fontSize: 11 }}
            />
        </Box>
    );
}
